import {autorun, makeAutoObservable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {
  ChannelCourseResource,
  ChannelExpandedCommon,
} from '../../../../api/channel';
import type {
  MembershipApiModel,
  MembershipExpandedCommon,
} from '../../../../api/membership';
import type {
  CoreClientMembershipsListParams,
  CoreCoachMembershipsListParams,
} from '../../../../api/types/core/methods';
import type {Destructible, WithReactionDispose} from '../../../../utils/store';
import type {Config, Member} from './types';

import {channelExpandCommon, channelStore} from '../../../../api/channel';
import {membershipExpandCommon} from '../../../../api/membership';
import {currentUserStore} from '../../../../api/user';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {isUserHasRole} from '../../../../utils/entity/user';
import {envManager} from '../../../../utils/env';
import {ReactionDisposeManager} from '../../../../utils/store';

/**
 * ChannelMembersStore store
 */
export class ChannelMembersStore implements Destructible, WithReactionDispose {
  static DEFAULT_LIMIT = 20;
  channel: ChannelExpandedCommon | null = null;
  channelId: Config['channelId'];
  disposer = new ReactionDisposeManager();
  isFetching = false;
  isLoaded = false;
  limit: Config['limit'];
  listStore = createCollectionStore<
    MembershipExpandedCommon,
    CoreClientMembershipsListParams | CoreCoachMembershipsListParams
  >({});
  members: (Member | null)[] = [];

  constructor(config: Config) {
    this.channelId = config.channelId;
    this.limit = config.limit || ChannelMembersStore.DEFAULT_LIMIT;

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    this.disposer.watch(
      'members',
      autorun(() => {
        // triggers observer
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.listStore.isLoaded;

        let members: (Member | null)[] = [];

        if (this.channel) {
          if (this.channel?.type === 'course') {
            if (this.listStore.isLoaded) {
              members = (
                (this.channel?.resource as ChannelCourseResource | null) || {
                  expandedCoaches: [],
                }
              ).expandedCoaches
                .slice()
                .concat(
                  this.listStore.items.map(membership => membership.user),
                );
            }
          } else {
            members = this.channel?.members.slice();
          }
        }

        runInAction(() => {
          this.members = members.map(member => {
            let label: string | undefined;

            if (member?._id === currentUserStore.user?._id) {
              label = i18n.t('shared:label.me');
            } else if (
              this.channel?.resource?.coach_ids.includes(member?._id || '')
            ) {
              if (this.channel?.resource?.user_id === member?._id) {
                label = i18n.t('shared:label.coach');
              } else {
                label = i18n.t('shared:label.co-coach');
              }
            }

            if (member) {
              return {
                ...member,
                label,
              };
            }

            return member;
          });

          if (this.channel?.type === 'support' && !isUserHasRole('support')) {
            this.members = this.members.filter(
              member => member?._id === currentUserStore.user?._id,
            );
          }
        });
      }),
    );
  }

  destruct() {
    this.disposer.dispose();

    this.listStore.clear();
  }

  async fetch(silent = false) {
    const channel = (await channelStore.fetch({
      _id: this.channelId,
      expand: channelExpandCommon,
    })) as ChannelExpandedCommon;

    runInAction(() => {
      this.channel = channel;
    });

    if (channel.type === 'course') {
      const userIsCoach = !!channel.resource?.coach_ids.includes(
        currentUserStore.user?._id || '',
      );

      this.listStore.setMethod(
        userIsCoach ? 'coach.memberships.list' : 'client.memberships.list',
      );

      await this.listStore.fetch(
        {
          ...this.storeParams,
          limit: Math.max(this.listStore.items.length, this.limit!),
        },
        {silent},
      );
    }

    runInAction(() => {
      this.isLoaded = true;
    });
  }

  async fetchMore() {
    if (this.listStore.hasMore) {
      await this.listStore.fetch(
        {
          ...this.storeParams,
          offset: this.listStore.items.length,
        },
        {
          silent: true,
        },
      );
    }
  }

  private get storeParams() {
    const params:
      | CoreClientMembershipsListParams
      | CoreCoachMembershipsListParams = {
      expand: membershipExpandCommon,
      limit: this.limit,
      query: [
        ['course_id', '==', this.channel?.resource_id || 'unknown'],
        [
          'status',
          '!in',
          [
            'proposed',
            'requested',
            envManager.isCore ? 'declined' : null,
          ].filter(Boolean) as MembershipApiModel['status'][],
        ],
      ],
    };

    return params;
  }
}
